import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// We use Bearer tokens in sessionStorage as the PRIMARY auth carrier.
// Cookies are not reliable cross-domain on mobile Chrome / iPad — they get
// blocked by browser-level cross-site cookie protections. A token attached
// via the Authorization header avoids that entirely.
//
// sessionStorage is scoped to the tab — closing the tab signs the user out.
// (Use localStorage if you want "stay signed in across tabs and restarts".)
const TOKEN_KEY = "auth_token";

const _memoryFallback = {};

export const tokenStore = {
  get() {
    try {
      const stored = sessionStorage.getItem(TOKEN_KEY);
      if (stored) return stored;
    } catch { /* fall through to memory */ }
    return _memoryFallback.token || null;
  },
  set(token) {
    try {
      if (token) sessionStorage.setItem(TOKEN_KEY, token);
      else sessionStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      // sessionStorage can throw in some private modes / sandboxed iframes.
      // Fall back to an in-memory store so the current session still works
      // (the user will need to log in again after a reload — better than
      // not being able to log in at all).
      console.warn("sessionStorage unavailable, using in-memory fallback:", e);
      if (token) _memoryFallback.token = token;
      else delete _memoryFallback.token;
    }
  },
  clear() {
    try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    delete _memoryFallback.token;
  },
};

const api = axios.create({
  baseURL: API,
  // IMPORTANT: do NOT set withCredentials. We use Bearer tokens (in the
  // Authorization header), not cookies, so credentials are unnecessary —
  // and setting withCredentials:true with a wildcard CORS origin (which is
  // the case on the Emergent native deployment) causes mobile Chrome / iOS
  // Safari to reject the request silently per the CORS spec:
  //   "A wildcard origin '*' cannot be used with credentials."
  // The backend continues to set/clear the auth cookie for backwards
  // compatibility, but the browser will ignore those Set-Cookie headers
  // when credentials are not requested — which is exactly what we want.
});

api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On a 401 from a protected endpoint, drop the stale token so the app falls
// back to the unauthenticated state rather than retrying forever.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err.response?.status === 401) {
      tokenStore.clear();
    }
    return Promise.reject(err);
  },
);

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Convert an axios error into a friendly, actionable message. Critically,
// distinguishes between "server rejected the request" (we have e.response)
// and "the request never reached the server / browser blocked it" (no
// e.response — usually a CORS, network, or offline issue).
export function describeApiError(err) {
  if (!err) return "Something went wrong. Please try again.";
  // The server responded with a structured error body — surface its detail.
  if (err.response?.data?.detail != null) {
    return formatApiErrorDetail(err.response.data.detail);
  }
  if (err.response?.status) {
    return `Server returned ${err.response.status}. Please try again.`;
  }
  // No response at all → network / CORS / DNS / offline. axios sets
  // err.message to "Network Error" in this case.
  if (err.message === "Network Error") {
    return "Can't reach the server. Check your connection and try again.";
  }
  return err.message || "Something went wrong. Please try again.";
}
